<template>
  <div class="protocol-tariff">
    <div class="row">
      <div class="col-lg-10">
        <selectlist
          :options="tariffs"
          v-model="tariffForm.tariff_id"
          label="Tariff"
          name="tariff"
          placeholder="Please Select"
          helper="Select a tariff to automatically apply default hire and storage rates"
          :show-error="tariffForm.errors.has('tariff_id')"
          :style="'align-items:flex-start'"
          :readonly="readonly"
        ></selectlist>
      </div>
      <div class="col-lg-2" v-if="!readonly">
        <form-button :indicator="processing" @clicked="submit">
          Save
        </form-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    incident: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      processing: false,
      tariffForm: new window.Form({
        tariff_id: null
      }),
      crossHireTariff: null,
      protocolTariffs: [],
      gtaNonGtaTariffs: []
    };
  },

  computed: {
    tariffs() {
      return [
        ...[this.crossHireTariff],
        ...this.protocolTariffs,
        ...this.gtaNonGtaTariffs
      ].filter(tariff => !!tariff);
    }
  },

  mounted() {
    Promise.all([this.getIncidentTariffs(), this.getGtaNonGtaTariffs()]).then(
      () => {
        this.setTariff(this.incident);
      }
    );
  },

  methods: {
    getIncidentTariffs() {
      return window.Ajax.get(
        `/hires/tariffs?filter[incident_id]=${this.incident.identifier}`
      )
        .then(response => {
          this.crossHireTariff = response.data.find(
            tariff => !!tariff.company_id
          );
          this.protocolTariffs = response.data.filter(
            tariff => !!tariff.insurer_id
          );
        })
        .catch(error => {});
    },

    getGtaNonGtaTariffs() {
      const types = ['gta', 'non_gta'];

      if (this.incident?.source?.incident_claim_type?.name === 'intervention') {
        types.push('intervention');
      }

      return window.Ajax.get(`/hires/tariffs?filter[type]=${types.join(',')}`)
        .then(response => {
          this.gtaNonGtaTariffs = response.data;
        })
        .catch(error => {});
    },

    setTariff(incident) {
      // If incident already has a tariff_id, set the tariff_id
      if (incident.tariff_id) {
        this.tariffForm.tariff_id = incident.tariff_id;
        return;
      }

      // For cross-hire incidents, use the cross-hire tariff if available
      if (incident.source?.incident_claim_type?.name === 'cross_hire') {
        if (this.crossHireTariff) {
          this.tariffForm.tariff_id = this.crossHireTariff.id;
          return;
        }
      }

      // For intervention incidents, use the intervention tariff if available
      if (this.incident.source?.incident_claim_type?.name === 'intervention') {
        const interventionTariff = this.gtaNonGtaTariffs.find(
          tariff => tariff.name === 'intervention'
        );

        if (interventionTariff) {
          this.tariffForm.tariff_id = interventionTariff.id;
          return;
        }
      }

      // If protocol tariffs exist, prioritise thirdParty, then client, then first available
      if (this.protocolTariffs.length > 0) {
        // Prioritise third-party protocol tariff
        const thirdPartyInsurerIds =
          incident.third_parties
            ?.map(party => party.insurer?.id)
            .filter(id => id != null) || [];

        const thirdPartyTariff = this.protocolTariffs.find(tariff =>
          thirdPartyInsurerIds.includes(tariff.insurer_id)
        );

        if (thirdPartyTariff) {
          this.tariffForm.tariff_id = thirdPartyTariff.id;
          return;
        }

        //Fallback to client protocol tariff
        const clientInsurerId = incident.client?.insurance?.insurer?.id;
        if (clientInsurerId) {
          const clientTariff = this.protocolTariffs.find(
            tariff => tariff.insurer_id === clientInsurerId
          );
          if (clientTariff) {
            this.tariffForm.tariff_id = clientTariff.id;
            return;
          }
        }

        // Fallback to first protocol tariff
        this.tariffForm.tariff_id = this.protocolTariffs[0].id;
        return;
      }

      // Check GTA status, true if thirdParty insurer is GTA
      const isGta =
        incident.third_parties?.some(party => party.insurer?.gta === true) ||
        false;

      const tariffName = isGta ? 'gta' : 'non_gta';

      const defaultTariff = this.gtaNonGtaTariffs.find(
        tariff => tariff.name === tariffName
      );

      this.tariffForm.tariff_id = defaultTariff ? defaultTariff.id : null;
    },

    submit() {
      if (this.readonly) return;
      this.processing = true;

      this.tariffForm
        .put(`incidents/${this.incident.identifier}`)
        .then(response => {
          this.processing = false;
          this.$store.dispatch('Incident/retrieve', this.incident.identifier);
          window.toastify.success(
            'Saved!',
            'The tariff was saved successfully!'
          );
        })
        .catch(error => {
          this.processing = false;
          window.swal(
            'Oops...',
            'Sorry, there was a problem saving the tariff.',
            'error'
          );
        });
    }
  }
};
</script>
